// Copyright (c) 2017 Deere & Company

import AmountCellRenderer from '../../../reports/shared/report-cell-renderers/amount-cell-renderer';
import ClaimNumberCellRenderer from '../components/cell-renderers/claim-number-renderer';
import CreditMemoCellRenderer from '../../../reports/shared/report-cell-renderers/credit-memo-cell-renderer';
import DateCellRenderer from '../../../reports/shared/report-cell-renderers/date-cell-renderer';
import classNames from '../../../reports/shared/constants/ag-grid-class-names';
import dateFilterParams from '../../../reports/shared/constants/date-filter-params';
import {formatClaimNumber} from '../../../shared/services/format-claim-number';
import {translate} from '../../../shared/services/translation-service';

export const getColumnDefinitions = () =>
    [
        {
            cellClass: classNames.cellLeftClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'account',
            field: 'account',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('ACCOUNT'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'city',
            field: 'city',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('DEALER_CITY'),
            width: 300
        },
        {
            cellClass: classNames.cellClassName,
            cellRendererFramework: DateCellRenderer,
            colId: 'dateIssued',
            field: 'postingDate',
            filter: 'date',
            filterParams: dateFilterParams,
            headerClass: classNames.headerClassName,
            headerName: translate('DATE_ISSUED'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            cellRendererFramework: ClaimNumberCellRenderer,
            colId: 'claimNumber',
            field: 'claimNumber',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('CLAIM_NUMBER'),
            valueGetter: params => formatClaimNumber(params.data.claimNumber),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            cellRendererFramework: CreditMemoCellRenderer,
            colId: 'creditMemo',
            field: 'creditMemoNumber',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('CREDIT_MEMO'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'pipNumber',
            field: 'pipNumber',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('PIP_NUMBER'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'pin',
            field: 'pin',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('PIN'),
            width: 200
        },
        {
            cellClass: classNames.cellClassName,
            cellRendererFramework: DateCellRenderer,
            colId: 'failureDate',
            field: 'failureDate',
            filter: 'date',
            filterParams: dateFilterParams,
            headerClass: classNames.headerClassName,
            headerName: translate('FAILURE_DATE'),
            width: 150
        },
        {
            cellClass: classNames.cellClassName,
            cellRendererFramework: DateCellRenderer,
            colId: 'repairDate',
            field: 'repairDate',
            filter: 'date',
            filterParams: dateFilterParams,
            headerClass: classNames.headerClassName,
            headerName: translate('REPAIR_DATE'),
            width: 150
        },
        {
            cellClass: classNames.cellClassName,
            cellRendererFramework: DateCellRenderer,
            colId: 'originalSubmissionDate',
            field: 'originalSubmissionDate',
            filter: 'date',
            filterParams: dateFilterParams,
            headerClass: classNames.headerClassName,
            headerName: translate('ORIGINAL_SUBMISSION_DATE'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'claimTypeDescription',
            field: 'claimTypeDescription',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('CLAIM_TYPE'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'subClaimTypeDescription',
            field: 'subClaimTypeDescription',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('SUB_CLAIM_TYPE'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'programName',
            field: 'programName',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('PROGRAM'),
            valueGetter: params => params.data.programName ? `${params.data.programName} - ${params.data.programDescription}` : '',
            width: 240
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'failurePartNumber',
            field: 'failurePartNumber',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('FAILURE_PART'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'referenceNumber1',
            field: 'referenceNumber1',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('REFERENCE_NUMBER1'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'repairNumber',
            field: 'repairNumber',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('REPAIR_NUMBER'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'referenceNumber2',
            field: 'referenceNumber2',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('REFERENCE_NUMBER2'),
            width: 150
        },
        {
            cellClass: classNames.cellLeftClassName,
            colId: 'customerName',
            field: 'customerName',
            filter: 'text',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('CUSTOMER_NAME'),
            width: 300
        },
        {
            cellClass: classNames.cellClassName,
            colId: 'currency',
            field: 'currency',
            filter: 'text',
            headerClass: classNames.headerClassName,
            headerName: translate('CURRENCY'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'requestedParts',
            field: 'requestedParts',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('REQUESTED_PARTS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedParts',
            field: 'approvedParts',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_PARTS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'unpaidParts',
            field: 'unpaidParts',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('UNPAID_PARTS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedPartsAdditive',
            field: 'approvedPartsAdditive',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_PARTS_ADDITIVE'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'unpaidPartsAdditive',
            field: 'unpaidPartsAdditive',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('UNPAID_PARTS_ADDITIVE'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'partsAdjustment',
            field: 'partsAdjustment',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('PARTS_ADJUSTMENT'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'requestedLaborAmount',
            field: 'requestedLaborAmount',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('REQUESTED_LABOR_AMOUNT'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedLaborAmount',
            field: 'approvedLaborAmount',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_LABOR_AMOUNT'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'unpaidLabor',
            field: 'unpaidLabor',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('UNPAID_LABOR'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedLaborAdditive',
            field: 'approvedLaborAdditive',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_LABOR_ADDITIVE'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'unpaidLaborAdditiveExcludingPips',
            field: 'unpaidLaborAdditiveExcludingPips',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('UNPAID_LABOR_ADDITIVES_EXCLUDING_PIPS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'unpaidLaborAdditivePips',
            field: 'unpaidLaborAdditivePips',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('UNPAID_LABOR_ADDITIVES_PIPS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'laborAdjustment',
            field: 'laborAdjustment',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('LABOR_ADJUSTMENT'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'requestedOtherCredit',
            field: 'requestedOtherCredit',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('REQUESTED_OTHER_CREDITS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedOtherCredits',
            field: 'approvedOtherCredit',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_OTHER_CREDITS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'unpaidOtherCredits',
            field: 'unpaidOtherCredits',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('UNPAID_OTHER_CREDITS'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'otherCreditAdjustment',
            field: 'otherCreditAdjustment',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('OTHER_CREDIT_ADJUSTMENT'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedTravelTotal',
            field: 'approvedTravelTotal',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_TRAVEL_TOTAL'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'unpaidTravel',
            field: 'unpaidTravel',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('UNPAID_TRAVEL'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedCoreValue',
            field: 'approvedCoreValue',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_CORE_VALUE'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'approvedOnshelfCost',
            field: 'approvedOnshelfCost',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('APPROVED_ON_SHELF_COST'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'totalRequestedClaimValue',
            field: 'totalRequestedClaimValue',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('TOTAL_REQUESTED_CLAIM_VALUE'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'deductible',
            field: 'deductible',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('DEDUCTIBLE'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'totalApprovedClaimValue',
            field: 'totalApprovedClaimValue',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('TOTAL_APPROVED_CLAIM_VALUE'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'totalApprovedIncludingTaxes',
            field: 'totalApprovedIncludingTaxes',
            filter: 'number',
            headerClass: classNames.headerRightClassName,
            headerName: translate('TOTAL_APPROVED_INCLUDING_TAXES'),
            width: 150
        },
        {
            cellClass: classNames.cellRightClassName,
            cellRendererFramework: AmountCellRenderer,
            colId: 'totalUnpaid',
            field: 'totalUnpaid',
            filter: 'number',
            filterParams: {apply: true},
            headerClass: classNames.headerRightClassName,
            headerName: translate('TOTAL_UNPAID'),
            width: 150
        }
    ];



// WEBPACK FOOTER //
// ./src/reimbursement-summary/claims/constants/column-definitions.js